import NoImage from '../../../images/noun-image.svg';
import { getIsLowestPrice } from '../../../utils/material/get-is-lowest-price';

type MaterialAuthorDto = {
    id?: number;
    image_path?: string;
    public_name?: string;
    user_id?: number;
};

type MaterialSaleDto = {
    expire_at?: string;
    material_id?: number;
    original_price?: number;
    price?: number;
    start_at?: string;
};

type MaterialUnitDto = {
    _id?: string;
    draft_locale?: string;
    next_update_draft?: string;
};

type MaterialDto = {
    _total?: number;
    acl_bundle_id?: number;
    acl_created_at?: string;
    acl_id?: number | null;
    acl_token?: string;
    acl_type?: string;
    author?: MaterialAuthorDto;
    average_rating?: number;
    bundle_price_without_discount?: number;
    cover_material_file_path?: FirstPreviewImage;
    created_at?: string;
    description?: string;
    file_types?: string;
    id?: number;
    interactive_id?: string;
    is_bundle?: boolean;
    is_high_quality?: boolean;
    is_shadow?: boolean;
    is_unit_publishable?: boolean;
    material_class_grades?: MaterialClassGrade[];
    material_files?: MaterialFile[];
    price?: number;
    prices?: Record<string, PricesItem>;
    related_materials?: string[];
    sale?: MaterialSaleDto;
    school_id?: string;
    short_description?: string;
    slug?: string;
    title?: string;
    top_categories?: MaterialTopCategory[];
    total_bundle_materials?: number;
    total_feedbacks?: number;
    total_pages?: number;
    unit?: MaterialUnitDto;
    url?: string;
    user_material_feedback_exists?: boolean;
    world?: string;
};

export type MaterialsGraphqlDto = {
    materials?: MaterialDto[];
};

const toMaterial = (dto: MaterialDto): Material => {
    const author = dto.author;
    const shouldResolveAuthorId = Boolean(author?.id || author?.user_id);

    return {
        author: {
            ...author,
            ...(shouldResolveAuthorId ? { id: author?.user_id ?? author?.id } : {}),
            details: {
                imagePath: author?.image_path || NoImage,
                publicName: author?.public_name
            }
        },
        averageRating: dto.average_rating,
        totalFeedbacks: dto.total_feedbacks,
        bundle: dto.is_bundle,
        bundleListTotal: dto.total_bundle_materials,
        bundleMaterialsPrice: dto.bundle_price_without_discount,
        description: dto.short_description ?? dto.description,
        firstPreviewImage: { ...dto.cover_material_file_path },
        id: dto.id,
        materialFiles: dto.material_files,
        price: dto.sale ? dto.sale?.price : dto.price,
        sale: dto.sale
            ? {
                  ...dto.sale,
                  original_price: dto.sale?.original_price ? dto.sale?.original_price : dto.price
              }
            : null,
        slug: dto.slug,
        title: dto.title,
        totalPages: dto.total_pages,
        aclId: dto.acl_id,
        aclBundleId: dto.acl_bundle_id,
        aclType: dto.acl_type,
        aclToken: dto.acl_token,
        aclCreatedAt: dto.acl_created_at,
        schoolId: dto.school_id,
        isShadow: dto.is_shadow,
        isHighQuality: dto.is_high_quality,
        isLowestPrice: getIsLowestPrice(dto as unknown as Material),
        prices: dto?.prices || null,
        createdAt: dto.created_at,
        interactiveId: dto.interactive_id,
        relatedMaterials: dto?.related_materials,
        materialTopCategories: dto?.top_categories,
        materialClassGrades: dto.material_class_grades,
        fileTypes: dto.file_types,
        url: dto.url,
        world: dto.world,
        userMaterialFeedbackExists: dto.user_material_feedback_exists,
        _total: dto._total,
        isUnitPublishable: dto.is_unit_publishable,
        ...(dto.unit
            ? {
                  unit: {
                      _id: dto.unit._id,
                      public: dto.is_unit_publishable,
                      nextUpdateDraft: dto.unit.next_update_draft,
                      draftLocale: dto.unit.draft_locale
                  }
              }
            : {})
    } as unknown as Material;
};

export const toMaterialsFromGraphql = (response: MaterialsGraphqlDto): Material[] =>
    (response?.materials ?? []).map(toMaterial);
